// DEV-332: Reports > Advanced tab metrics, computed from data the page already
// loads. Each returns null when there's nothing to compute from, so the tile
// shows "—" instead of a made-up number.

type DateLike = string | Date | null | undefined;
type ClientLike = { id: number; status?: string | null; clientSince?: DateLike; createdAt?: DateLike };
type ProspectLike = { clientId?: number | null; createdAt?: DateLike };
type ClientPlanLike = {
  clientId: number;
  status?: string | null;
  isActive?: boolean | null;
  plan?: { type?: string | null } | null;
};

const DAY_MS = 24 * 60 * 60 * 1000;

function toDate(v: DateLike): Date | undefined {
  if (!v) return undefined;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** Same date the client header shows as "Client since": clientSince, else createdAt. */
export function clientStartDate(c: ClientLike): Date | undefined {
  return toDate(c.clientSince) ?? toDate(c.createdAt);
}

const isStatus = (c: ClientLike, s: string) => String(c.status ?? "").toLowerCase() === s;

/**
 * Clients that started before `periodStart` (active or inactive; pending never
 * started) and are still active, as a % of them. Clients carry no termination
 * date, so an inactive client counts as lost whenever it left.
 */
export function retentionRate(clients: readonly ClientLike[], periodStart: Date): number | null {
  const base = clients.filter(c => {
    if (!isStatus(c, "active") && !isStatus(c, "inactive")) return false;
    const start = clientStartDate(c);
    return !!start && start < periodStart;
  });
  if (base.length === 0) return null;
  return (base.filter(c => isStatus(c, "active")).length / base.length) * 100;
}

/** Mean years since "Client since" across active clients. */
export function avgTenureYears(clients: readonly ClientLike[], now: Date): number | null {
  const years = clients
    .filter(c => isStatus(c, "active"))
    .map(clientStartDate)
    .filter((d): d is Date => !!d && d <= now)
    .map(d => (now.getTime() - d.getTime()) / (365.25 * DAY_MS));
  if (years.length === 0) return null;
  return years.reduce((s, y) => s + y, 0) / years.length;
}

/**
 * This month's billed premium vs the month before the selected period
 * (year: vs January). `monthlyRevenue` is ytdRevenueByMonth: index 0 = January
 * … current month, so a base before January has no data.
 */
export function premiumGrowthRate(
  monthlyRevenue: readonly number[],
  period: string,
  now: Date,
): { rate: number; baseMonth: number } | null {
  const current = now.getMonth();
  const baseMonth =
    period === "year" ? 0 : period === "quarter" ? Math.floor(current / 3) * 3 - 1 : current - 1;
  if (baseMonth < 0 || baseMonth >= current) return null;
  const base = monthlyRevenue[baseMonth] ?? 0;
  const latest = monthlyRevenue[current];
  if (!(base > 0) || latest == null) return null;
  return { rate: ((latest - base) / base) * 100, baseMonth };
}

/** Mean days from prospect created to its converted client's "Client since". */
export function avgCloseDays(prospects: readonly ProspectLike[], clients: readonly ClientLike[]): number | null {
  const byId = new Map(clients.map(c => [c.id, c]));
  const days: number[] = [];
  for (const p of prospects) {
    const client = p.clientId != null ? byId.get(p.clientId) : undefined;
    const opened = toDate(p.createdAt);
    const won = client && clientStartDate(client);
    if (!opened || !won || won < opened) continue;
    days.push((won.getTime() - opened.getTime()) / DAY_MS);
  }
  if (days.length === 0) return null;
  return days.reduce((s, d) => s + d, 0) / days.length;
}

/** Total annual premium ÷ active clients. */
export function avgBookSize(annualPremium: number, activeClients: number): number | null {
  return activeClients > 0 ? annualPremium / activeClients : null;
}

// Plan types per coverage line (shared/schemas/planTypes.ts SECTION_ORDER, isLifeOrDisability)
const CROSS_SELL_LINES = {
  vision: ["vision"],
  life: ["life", "add", "voluntary_life"],
  disability: ["std", "ltd", "disability"],
} as const;

export type CrossSellLine = keyof typeof CROSS_SELL_LINES;

/** Active clients with no active client plan in each coverage line. */
export function crossSellGaps(
  clients: readonly ClientLike[],
  clientPlans: readonly ClientPlanLike[],
): Record<CrossSellLine, number> {
  const typesByClient = new Map<number, Set<string>>();
  for (const cp of clientPlans) {
    if (cp.isActive === false || String(cp.status ?? "active").toLowerCase() !== "active") continue;
    const type = String(cp.plan?.type ?? "").toLowerCase();
    if (!type) continue;
    if (!typesByClient.has(cp.clientId)) typesByClient.set(cp.clientId, new Set());
    typesByClient.get(cp.clientId)!.add(type);
  }
  const active = clients.filter(c => isStatus(c, "active"));
  const gaps = {} as Record<CrossSellLine, number>;
  for (const line of Object.keys(CROSS_SELL_LINES) as CrossSellLine[]) {
    const lineTypes: readonly string[] = CROSS_SELL_LINES[line];
    gaps[line] = active.filter(c => {
      const types = typesByClient.get(c.id);
      return !types || !lineTypes.some(t => types.has(t));
    }).length;
  }
  return gaps;
}
